/**
 * Projects Schema
 * Project and task management tables
 */

import { pgTable, uuid, varchar, text, timestamp, boolean } from 'drizzle-orm/pg-core';
import { relations } from 'drizzle-orm';
import { profiles } from './profiles';

// =============================================================================
// Projects Table
// =============================================================================
export const projects = pgTable('projects', {
  id: uuid('id').primaryKey().defaultRandom(),
  name: varchar('name', { length: 255 }).notNull(),
  type: varchar('type', { length: 100 }).notNull(),
  status: varchar('status', { length: 50 }).notNull().default('active'), // 'active' | 'inactive' | 'archived'
  ownerId: uuid('owner_id').notNull(),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// =============================================================================
// Tasks Table
// =============================================================================
export const tasks = pgTable('tasks', {
  id: uuid('id').primaryKey().defaultRandom(),
  title: varchar('title', { length: 255 }).notNull(),
  description: text('description'),
  status: varchar('status', { length: 50 }).notNull().default('pending'),
  createdBy: uuid('created_by').notNull(),
  assignedTo: uuid('assigned_to'),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// =============================================================================
// Project Tasks (Junction Table)
// =============================================================================
export const projectTasks = pgTable('project_tasks', {
  projectId: uuid('project_id').notNull(),
  taskId: uuid('task_id').notNull(),
  createdAt: timestamp('created_at').defaultNow(),
});

// =============================================================================
// Project Comments
// =============================================================================
export const projectComments = pgTable('project_comments', {
  id: uuid('id').primaryKey().defaultRandom(),
  projectId: uuid('project_id').notNull(),
  userId: uuid('user_id').notNull(),
  content: text('content').notNull(),
  createdAt: timestamp('created_at').defaultNow(),
  updatedAt: timestamp('updated_at').defaultNow(),
});

// =============================================================================
// Designs Table
// =============================================================================
export const designs = pgTable('designs', {
  id: uuid('id').primaryKey().defaultRandom(),
  title: varchar('title', { length: 255 }).notNull(),
  description: text('description'),
  content: text('content').default('{}'), // JSON stored as text
  ownerId: uuid('owner_id').notNull(),
  isPublic: boolean('is_public').default(false),
  specVersion: varchar('spec_version', { length: 50 }),
  deploymentTarget: varchar('deployment_target', { length: 100 }),
  runtimeConfig: text('runtime_config'), // JSON
  exportFormat: text('export_format'), // JSON
  validationSchemaId: uuid('validation_schema_id'),
  createdAt: timestamp('created_at').defaultNow().notNull(),
  updatedAt: timestamp('updated_at').defaultNow().notNull(),
});

// =============================================================================
// Relations
// =============================================================================
export const projectsRelations = relations(projects, ({ one, many }) => ({
  owner: one(profiles, {
    fields: [projects.ownerId],
    references: [profiles.id],
  }),
  tasks: many(projectTasks),
  comments: many(projectComments),
}));

export const tasksRelations = relations(tasks, ({ one, many }) => ({
  createdByUser: one(profiles, {
    fields: [tasks.createdBy],
    references: [profiles.id],
  }),
  assignedToUser: one(profiles, {
    fields: [tasks.assignedTo],
    references: [profiles.id],
  }),
  projects: many(projectTasks),
}));

export const designsRelations = relations(designs, ({ one }) => ({
  owner: one(profiles, {
    fields: [designs.ownerId],
    references: [profiles.id],
  }),
}));

// =============================================================================
// Types
// =============================================================================
export type Project = typeof projects.$inferSelect;
export type NewProject = typeof projects.$inferInsert;
export type Task = typeof tasks.$inferSelect;
export type NewTask = typeof tasks.$inferInsert;
export type Design = typeof designs.$inferSelect;
export type NewDesign = typeof designs.$inferInsert;
